import { RrAlert } from '@rr-crm/shared/components';
import { useQueryClient } from '@tanstack/react-query';
import { type RefObject, useEffect, useRef } from 'react';

import { exitViewAsSalesRep } from '../auth/viewAs';
import { useViewingAsSalesRep } from '../auth/viewAsStorage';

/**
 * T-VIEW-AS — persistent "Viewing as Sales Rep" banner.
 *
 * Fixed to the BOTTOM of the page, red (the `--rr-state-danger` destructive
 * palette via `RrAlert variant="error"` — NOT the orange destructive *button*).
 * Renders only while this tab is impersonating. The "Exit View As" control is
 * client-authoritative: it best-effort fires the exit bookend, ALWAYS clears
 * the per-tab flag, refetches `/me/profile` IN PLACE so the nav re-renders as
 * the real superAdmin, and drops the salesRep-scoped data fetched during the
 * impersonation window (commission statements, dashboards, search results) so
 * it cannot linger under the restored admin nav — one action, no re-auth,
 * MSAL untouched.
 *
 * RIG-370: we must NOT `queryClient.clear()` then invalidate `['me','profile']`.
 * `clear()` removes the profile query, leaving the App-level `useUserProfile`
 * observer with no data, and the subsequent invalidate has nothing to refetch —
 * so the left nav stayed stuck on the stale sales-rep roles until a manual page
 * refresh. Instead we refetch the still-cached profile query in place (its
 * observer is preserved → the nav re-renders immediately), then remove every
 * OTHER query (RIG-314's no-lingering-data intent).
 */
const isProfileKey = (key: ReadonlyArray<unknown>): boolean =>
  key[0] === 'me' && key[1] === 'profile';

/** Used when the banner cannot be measured (first paint, jsdom). */
export const VIEW_AS_FALLBACK_HEIGHT_PX = 96;

/**
 * Overnight D6 — keep page content clear of the fixed banner. While View As
 * is on, the document gets bottom padding equal to the banner's height, so
 * the last row of any page can scroll above it instead of sitting under it.
 * The previous padding is restored when the banner goes away.
 */
function useReserveBottomSpace(active: boolean): RefObject<HTMLDivElement> {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!active) return;
    // QA 2026-10-01 walkthrough: the shell's <main> is the element that
    // scrolls, not the body, so padding only the body still left the last
    // button of a page (Save account on New lead) under the banner. Pad both.
    const targets = [document.body, ref.current?.closest('main') ?? null].filter(
      (el): el is HTMLElement => el !== null,
    );
    const previous = targets.map((el) => el.style.paddingBottom);
    const apply = (): void => {
      const measured = ref.current?.offsetHeight ?? 0;
      const pad = `${measured > 0 ? measured : VIEW_AS_FALLBACK_HEIGHT_PX}px`;
      for (const el of targets) el.style.paddingBottom = pad;
    };
    apply();
    const observer =
      typeof ResizeObserver === 'function' && ref.current ? new ResizeObserver(apply) : null;
    if (observer && ref.current) observer.observe(ref.current);
    return () => {
      observer?.disconnect();
      targets.forEach((el, i) => {
        el.style.paddingBottom = previous[i] ?? '';
      });
    };
  }, [active]);
  return ref;
}

export function ViewAsBanner(): JSX.Element | null {
  const viewing = useViewingAsSalesRep();
  const queryClient = useQueryClient();
  const bannerRef = useReserveBottomSpace(viewing);

  if (!viewing) return null;

  const handleExit = async (): Promise<void> => {
    await exitViewAsSalesRep(); // clears the flag even if the endpoint errors
    // RIG-370: refetch the profile IN PLACE (don't clear() it) so its observer
    // survives and the nav re-renders as the restored superAdmin immediately.
    // The flag is already cleared, so this refetch carries no X-View-As-Role
    // header and returns the real admin roles.
    await queryClient.refetchQueries({ queryKey: ['me', 'profile'] });
    // RIG-314: drop the sales-rep-scoped data fetched during impersonation
    // (everything EXCEPT the just-restored profile) so it can't linger under
    // the admin nav; active views refetch fresh as superAdmin.
    queryClient.removeQueries({ predicate: (q) => !isProfileKey(q.queryKey) });
  };

  return (
    // Overnight D6 — opaque, and starts at the right edge of the 240px nav
    // rail on desktop (RrNav shows the rail from 768px, Tailwind's `md`), so
    // the rail's user chip and Sign out are never drawn under the banner.
    <div
      ref={bannerRef}
      className="fixed inset-x-0 bottom-0 z-[60] p-2 sm:p-3 md:left-[240px]"
      data-testid="view-as-banner"
    >
      <RrAlert variant="error" opaque className="shadow-lg">
        <div className="flex items-center justify-between gap-3">
          {/* RIG-483 — say that it is READ-ONLY. The banner named the mode but
              not its consequence, so a superAdmin who saw it still clicked
              Save and got a raw server rejection with nothing tying the two
              together. */}
          <span>
            <span className="font-semibold">Viewing as Sales Rep</span>
            <span className="block text-sm">
              Read-only. Saves, edits and deletes are blocked while this is on.
            </span>
          </span>
          <button
            type="button"
            onClick={() => void handleExit()}
            data-testid="view-as-exit"
            className="rounded border border-[var(--rr-state-danger)] px-3 py-1 text-sm font-semibold hover:bg-[color-mix(in_srgb,var(--rr-state-danger)_10%,transparent)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rr-teal-light"
          >
            Exit View As
          </button>
        </div>
      </RrAlert>
    </div>
  );
}
